import axiosInstance from "config/AxiosConfig";
import { ReportEngineListResp } from "domain/payload/response/reportEngine/ReportEngineResp";
import { ReportEngineDetailResp } from "domain/payload/response/reportEngine/ReportEngineDetailResp";
import { ReportEngineCreateReq } from "domain/payload/request/reportEngine/ReportEngineCreateReq";
import { ReportEngineUpdateReq } from "domain/payload/request/reportEngine/ReportEngineUpdateReq";
import { BaseResponse } from "domain/payload/response/BaseResponseDto";

const ReportEngineService = () => {
  const reportEngineList = async (
    token: string,
    page?: number,
    size?: number,
    search?: string | null,
    isFavorite?: boolean
  ): Promise<BaseResponse<ReportEngineListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/report-engine/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, size, search, isFavorite },
      });
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const reportEngineDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<ReportEngineDetailResp>> => {
    try {
      const response = await axiosInstance.get(
        `/v1/legacy/cms/report-engine/detail`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { id },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const reportEngineCreate = async (
    token: string,
    request: ReportEngineCreateReq
  ): Promise<BaseResponse<ReportEngineDetailResp>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/report-engine/create`,
        request,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const reportEngineUpdate = async (
    token: string,
    request: ReportEngineUpdateReq
  ): Promise<BaseResponse<ReportEngineDetailResp>> => {
    try {
      const response = await axiosInstance.put(
        `/v1/legacy/cms/report-engine/update`,
        request,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const reportEngineDelete = async (
    token: string,
    id: string
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.delete(
        `/v1/legacy/cms/report-engine/delete`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { id },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const reportEngineExecute = async (
    token: string,
    request: any
  ): Promise<any> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/report-engine/execute`,
        request,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const reportEngineView = async (
    token: string,
    request: { id: string; params?: Record<string, any> }
  ): Promise<BaseResponse<{ columns: string[]; rows: any[][] }>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/report-engine/view`,
        request,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  const reportEngineDownload = async (
    token: string,
    reportCode: string,
    params: Record<string, any> = {},
    extension: "csv" | "xlsx" = "csv"
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/report-engine/download`,
        params,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { reportCode, extension },
        }
      );

      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message || error;
    }
  };

  const reportEngineToggleFavorite = async (
    token: string,
    reportId: string,
    toggle: boolean
  ): Promise<BaseResponse<any>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/report-engine/toggle-favorite`,
        { reportId, toggle },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error.response?.data?.message || error.message;
    }
  };

  return {
    reportEngineList,
    reportEngineDetail,
    reportEngineCreate,
    reportEngineUpdate,
    reportEngineDelete,
    reportEngineExecute,
    reportEngineView,
    reportEngineDownload,
    reportEngineToggleFavorite,
  };
};

export default ReportEngineService;
